import { useIsPc } from "@/src/hooks";
import { useEffect, useRef, useState, useMemo } from "react";
import { cn } from "utils";
const MIN_HEIGHT = 2;
const MAX_HEIGHT = 24;

interface VoiceWaveformProps {
  analyserNode: AnalyserNode | null;
  className?: string;
}

export function VoiceWaveform({ analyserNode, className }: VoiceWaveformProps) {
  const [isPC] = useIsPc();
  const BAR_COUNT = isPC ? 200 : 60;
  const [amplitudes, setAmplitudes] = useState<number[]>(() =>
    Array(BAR_COUNT).fill(0),
  );
  const rafRef = useRef(0);
  // 平滑缓冲：避免振幅突变导致视觉闪烁
  const smoothRef = useRef<number[]>(Array(BAR_COUNT).fill(0));

  // BAR_COUNT 变化时重置平滑缓冲，避免旧数组长度与当前不一致
  useEffect(() => {
    smoothRef.current = Array(BAR_COUNT).fill(0);
  }, [BAR_COUNT]);

  const barDelays = useMemo(
    () => Array.from({ length: BAR_COUNT }, (_, i) => (i / BAR_COUNT) * 0.8),
    [BAR_COUNT],
  );

  useEffect(() => {
    if (!analyserNode) {
      return;
    }

    let active = true;
    const buf = new Uint8Array(analyserNode.frequencyBinCount);
    const step = Math.max(1, Math.floor(buf.length / BAR_COUNT));

    const loop = () => {
      if (!active) {
        return;
      }

      analyserNode.getByteTimeDomainData(buf);

      const smooth = smoothRef.current;
      const amps = new Array<number>(BAR_COUNT);
      for (let i = 0; i < BAR_COUNT; i++) {
        const val = buf[i * step] ?? 128;
        const raw = Math.abs(val - 128) / 128;
        // EMA 平滑：α=0.25 → 快速跟随同时保留平滑感
        smooth[i] = smooth[i] * 0.75 + raw * 0.25;
        // 幂次压缩：小信号大幅放大（0.4次方），大信号不过载
        amps[i] = Math.pow(smooth[i], 0.4);
      }
      setAmplitudes(amps);
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      active = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [analyserNode]);

  return (
    <div
      className={cn(
        "flex items-center gap-[3px] h-[24px] overflow-hidden flex-1",
        className,
      )}
    >
      {barDelays.map((delay, i) => {
        const boost = amplitudes[i] ?? 0;
        const dynamicMax =
          MIN_HEIGHT + (MAX_HEIGHT - MIN_HEIGHT) * Math.max(0.08, boost);

        return (
          <div
            key={i}
            className="w-[2.5px] rounded-full bg-content_secondary"
            style={{
              flexShrink: 0,
              animation: `voice-bar 3.5s ease-in-out ${delay}s infinite`,
              ["--bar-min" as string]: `${MIN_HEIGHT}px`,
              ["--bar-max" as string]: `${dynamicMax}px`,
            }}
          />
        );
      })}

      <style>{`
        @keyframes voice-bar {
          0%, 100% { height: var(--bar-min, 2px); }
          50% { height: var(--bar-max, 7px); }
        }
      `}</style>
    </div>
  );
}
